import { createSelector } from '@reduxjs/toolkit';
import { Box } from '@mui/material';
import { type AxiosResponse } from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { logError } from '../../../../../Utilities/logError';
import { NetworkRequest } from '../../../../Utilities/NetworkRequests/NetworkRequests';
import { type ICarriersBuyersHourlyData } from './types';
import {
  DATE_COLUMN,
  HOUR_COLUMN,
  TYWallBuyersCarriersHourlyColumns
} from './columns';
import { formatAffiliateValue } from '../../../../Utilities/getAffiliateIDFromName';
import { siteToLabel } from '../../../../Affiliates/Components/AffiliateSettings';

interface IStoreState {
  dateRange: {
    startDate: string;
    endDate: string;
    dateRange: string;
  };
  filters: {
    buyer: string;
    carriersHourly: string;
    affiliateID: string;
    site: string;
  };
}

export const LOCAL_STORAGE_CARRIERS_COMBINATION_HOURLY =
  'carriersCombinationHourly';

export const useAppStoreState = () => {
  return useSelector(
    createSelector(
      (state: IStoreState) => {
        return {
          site: state.filters.site,
          carriersHourly: state.filters.carriersHourly,
          buyer: state.filters.buyer,
          startDate: state.dateRange.startDate,
          endDate: state.dateRange.endDate,
          affiliateID: state.filters.affiliateID,
          dateRange: state.dateRange.dateRange
        };
      },
      (obj) => obj
    )
  );
};

export const useCarriersBuyersHourlyData = (
  type: 'carriers' | 'buyers' | 'combo'
) => {
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(false);
  const [rows, setRows] = useState<Array<ICarriersBuyersHourlyData>>([]);
  const [onlyOneDayAvailable, setOnlyOneDayAvailable] = useState(false);

  const [selectedDate, setSelectedDate] = useState('');
  const [hoursRows, setHoursRows] = useState<
    ICarriersBuyersHourlyData['hours']
  >([]);

  const storeState = useAppStoreState();
  const {
    site,
    carriersHourly,
    buyer,
    affiliateID,
    startDate,
    endDate,
    dateRange
  } = storeState;

  const changeSelectedDate = useCallback(
    (row: ICarriersBuyersHourlyData) => {
      setSelectedDate(`${row.date}`);
      setHoursRows(row.hours);
    },
    [setSelectedDate]
  );

  const back = useCallback(() => {
    setHoursRows([]);
    setSelectedDate('');
    setOnlyOneDayAvailable(false);
  }, []);

  useEffect(() => {
    if (
      (type === 'carriers' && carriersHourly === '-1') ||
      (type === 'buyers' && buyer === '-1')
    ) {
      setLoading(false);
      setLoadingError(false);
      setRows([]);
      setSelectedDate('');

      return;
    }

    (async () => {
      back();
      setLoading(true);
      setLoadingError(false);

      try {
        const params: Record<string, string> = {
          start_date: moment(startDate).format('YYYY-MM-DD'),
          end_date: moment(endDate).format('YYYY-MM-DD'),
          date_range: dateRange,
          affiliate_id: formatAffiliateValue(affiliateID)
        };

        if (type === 'buyers') {
          params.buyer = buyer;
        } else {
          params.provider = carriersHourly;
          params.site_id = site;
        }

        let data: Array<ICarriersBuyersHourlyData> = [];

        if (type === 'combo') {
          const { buyer, provider, site } = JSON.parse(
            localStorage.getItem(LOCAL_STORAGE_CARRIERS_COMBINATION_HOURLY) ||
              '{}'
          );

          const {
            data: responseData
          }: AxiosResponse<Array<ICarriersBuyersHourlyData>> =
            await NetworkRequest(
              'ty',
              {
                ...params,
                buyer,
                provider,
                site_id: site
              },
              'getTyWallCarriersHourly'
            );

          data = responseData;
        } else {
          const {
            data: responseData
          }: AxiosResponse<Array<ICarriersBuyersHourlyData>> =
            await NetworkRequest('ty', params, 'getTyWallCarriersHourly');

          data = responseData;
        }

        if (data.length === 1) {
          const selectedDate = data[0];

          changeSelectedDate({
            ...selectedDate,
            date: selectedDate.date
          });

          setOnlyOneDayAvailable(true);

          setLoading(false);
          setLoadingError(false);

          return;
        }

        const preparedData = data.map((item) => ({
          ...item,
          changeSelectedDate
        }));

        setRows(preparedData);
        setLoadingError(false);
      } catch (error) {
        setLoadingError(true);
        logError(error);
      } finally {
        setLoading(false);
      }
    })();
  }, [
    affiliateID,
    back,
    buyer,
    carriersHourly,
    changeSelectedDate,
    dateRange,
    endDate,
    site,
    startDate,
    type
  ]);

  const finalColumns = useMemo(() => {
    if (hoursRows?.length) {
      return [HOUR_COLUMN, ...TYWallBuyersCarriersHourlyColumns];
    }

    return [DATE_COLUMN, ...TYWallBuyersCarriersHourlyColumns];
  }, [hoursRows?.length]);

  const defaultOpenGroups = useMemo(() => {
    if (hoursRows?.length) {
      return [];
    }

    return Array.from(new Set(rows.map((r) => r.date)));
  }, [hoursRows?.length, rows]);

  const nameBlock = useMemo(() => {
    if (type === 'combo') {
      const comboLocalStorage = localStorage.getItem(
        LOCAL_STORAGE_CARRIERS_COMBINATION_HOURLY
      );

      const { buyer, provider, site } = JSON.parse(comboLocalStorage || '{}');

      return (
        <Box sx={{ margin: '20px 30px' }}>
          <b>Buyer:</b> {buyer !== '-1' ? buyer : 'Not selected'} |{' '}
          <b>Carrier:</b> {provider !== '-1' ? provider : 'Not selected'} |{' '}
          <b>Site:</b> {siteToLabel[site as keyof typeof siteToLabel] || site} |{' '}
          <b>Affiliate</b>: {affiliateID || 'All'}
        </Box>
      );
    }

    if (type === 'buyers') {
      return (
        <Box sx={{ margin: '20px 30px' }}>
          <b>Buyer:</b> {buyer !== '-1' ? buyer : 'Not selected'} |{' '}
          <b>Affiliate</b>: {affiliateID || 'All'}
        </Box>
      );
    }

    if (type === 'carriers') {
      return (
        <Box sx={{ margin: '20px 30px' }}>
          <b>Carrier:</b>{' '}
          {carriersHourly !== '-1' ? carriersHourly : 'Not selected'} |{' '}
          <b>Affiliate</b>: {affiliateID || 'All'}
        </Box>
      );
    }

    return null;
  }, [affiliateID, buyer, carriersHourly, type]);

  return useMemo(() => {
    return {
      loading,
      loadingError,
      onlyOneDayAvailable,
      rows: hoursRows?.length ? hoursRows : rows,
      selectedDate,
      nameBlock,
      defaultOpenGroups,
      finalColumns,
      back
    };
  }, [
    back,
    defaultOpenGroups,
    finalColumns,
    hoursRows,
    loading,
    loadingError,
    nameBlock,
    onlyOneDayAvailable,
    rows,
    selectedDate
  ]);
};
